import { useRouter } from 'next/router';
import React, { useRef, useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';

import ImageSlider from 'components/ImageSlider/ImageSlider';
import OvalCTA from 'components/OvalCTA/OvalCTA';
import useOverlayPortal from 'hooks/useOverlayPortal';
import { isIOS, isMobile } from 'u9/utils/platform';
import { setCursorLabel, triggerLightCursor } from 'utils/cursorController';
import { handleMouseLeave } from 'utils/cursorGlobal';
import { ROUTES } from 'utils/routes';
import { HeroCMS } from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from './Hero.styles';

interface BGVideo extends HTMLVideoElement {
  mozRequestFullScreen(): Promise<void>;
  webkitRequestFullscreen(): Promise<void>;
}

const Hero: React.FunctionComponent<HeroCMS> = ({
  media,
  mediaType,
  shortVideo,
  videoErrorMessage,
  videoType,
  id,
  bottomHeading,
  cursorLabelPlay = 'Play',
  cursorLabelClose = 'Close',
  ...rest
}) => {
  const [ready, setReady] = useState(false);
  const [fullScreen, setFullScreen] = useState<React.ReactNode>(<></>);
  const bgVideo = useRef<BGVideo>();
  const overlay = useOverlayPortal();

  const router = useRouter();

  useEffectOnce(() => setReady(true));

  const playCursor = () => {
    triggerLightCursor();
    setCursorLabel(cursorLabelPlay);
  };

  const closeCursor = () => {
    triggerLightCursor();
    setCursorLabel(cursorLabelClose);
  };

  const onPlayClickMobile = () => {
    const video = bgVideo.current;

    if (video.requestFullscreen) {
      video.requestFullscreen().then();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen().then();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen().then();
    }

    video.play().then();
  };

  const onPlayClick = nodes => {
    if (isMobile() && videoType?.source === 'self_hosted') {
      onPlayClickMobile();
    } else {
      setFullScreen(nodes);
      closeCursor();
    }
  };

  const fullScreenVideo = (
    <S.FullScreenWrapper>
      <S.FullScreenVideoBlock
        id={id}
        media={media[0]}
        videoSource={videoType?.source}
        videoSourceId={videoType?.sourceId}
        azureUserId={videoType?.azureUserId}
        videoErrorMessage={videoErrorMessage}
        autoplay
        mute={isIOS()}
        disableCursor={router.pathname === ROUTES.ABOUT}
        disableInView
        withSound={isIOS()}
      />
      <S.OverlayButton
        onClick={() => setFullScreen(<></>)}
        onMouseEnter={closeCursor}
        onMouseLeave={() => handleMouseLeave()}
        isFullScreen
      />
    </S.FullScreenWrapper>
  );

  const imagePreview = (
    <S.ContentMediaBlock mediaType="image" media={media[0]} />
  );

  const videoPreview = (
    <>
      {isMobile() && videoType?.source === 'self_hosted' && (
        <S.BackgroundVideo src={media[0]?.url} ref={bgVideo} />
      )}
      <S.VideoBlock
        id={id}
        media={shortVideo}
        videoSource={videoType?.source}
        videoErrorMessage={videoErrorMessage}
        videoSourceId={videoType?.sourceId}
        azureUserId={videoType?.azureUserId}
        vimeoBackground={videoType?.vimeoBackground}
        mute={videoType?.mute}
        autoplay={videoType?.autoplay}
        manualPlay={videoType?.manualPlay}
        azurePlaceholder={videoType?.azurePlaceholder}
        disableCursor={router.pathname === ROUTES.ABOUT}
      />
      <S.OverlayButton
        onClick={() => onPlayClick(fullScreenVideo)}
        onMouseEnter={playCursor}
        onMouseLeave={() => handleMouseLeave()}
      >
        {router.pathname === ROUTES.ABOUT && (
          <S.PlayButtonWrapper>
            <OvalCTA label="Play" />
          </S.PlayButtonWrapper>
        )}
      </S.OverlayButton>
    </>
  );

  const renderTypeOfMedia = () => {
    switch (mediaType) {
      case 'image':
        return imagePreview;
      case 'video':
        return videoPreview;
      case 'slider':
        return <ImageSlider media={media} />;
      default:
        return null;
    }
  };

  return (
    <S.HeroWrapper {...rest}>
      {renderTypeOfMedia()}

      {ready && overlay(fullScreen)}

      {bottomHeading && (
        <S.HeadingSlider>
          <S.BottomHeading text={bottomHeading} size="display" />
        </S.HeadingSlider>
      )}
    </S.HeroWrapper>
  );
};

export default Hero;
